import { useCallback, useEffect, useState } from 'react';
import { FormInstance, message } from 'antd';
import {
  useContactPersonReadQuery,
  useLanguageSelectListQuery,
  useSupplierSelectListForMultipleBrandsQuery,
  useSupplierSelectListQuery,
} from 'api';
import { useNotificationApi } from 'hooks/useNotificationApi';

export interface SupplierItem {
  supplierId: string;
  contactPersonId: string;
  languageId: string;
  supplierName: string;
  contactPersonName: string;
  languageName: string;
  flagLocation: string;
}

interface UseSupplierProps {
  form: FormInstance;
  brandIds?: string[];
  onSupplierAdded: (supplierId: string, contactPersonId: string, languageId: string) => Promise<void>;
}

export const useSuppliers = ({ form, brandIds, onSupplierAdded }: UseSupplierProps) => {
  const [suppliers, setSuppliers] = useState<SupplierItem[]>([]);
  const [selectedSupplierId, setSelectedSupplierId] = useState<string>('');
  const [selectedContactPersonId, setSelectedContactPersonId] = useState<string>('');
  const [selectedLanguageId, setSelectedLanguageId] = useState<string>('');
  const [disabledSuppActions, setDisabledSuppActions] = useState(true);
  const [openSupplierModal, setOpenSupplierModal] = useState(false);
  const [openContactPersonModal, setOpenContactPersonModal] = useState('');
  const notificationApi = useNotificationApi();

  // Get supplier options
  const { data: allSupplierOptions = [] } = useSupplierSelectListQuery();
  const { data: supplierOptions = [] } = useSupplierSelectListForMultipleBrandsQuery({ brandIds }, { skip: !brandIds });
  const { data: languagesList = [] } = useLanguageSelectListQuery();

  // Get contact persons for selected supplier
  const { data: contactPersons = [], isLoading: loadingContactPersons } = useContactPersonReadQuery(
    { entityId: selectedSupplierId },
    { skip: !selectedSupplierId }
  );

  // Filter suppliers not associated with the brand
  const brandSupplierIds = new Set(supplierOptions.map((item: any) => item.id));
  const filteredSuppliers = allSupplierOptions.filter((item: any) => !brandSupplierIds.has(item.id));

  useEffect(() => {
    if (selectedLanguageId && selectedSupplierId && selectedContactPersonId) {
      setDisabledSuppActions(false);
    } else {
      setDisabledSuppActions(true);
    }
  }, [selectedLanguageId, selectedSupplierId, selectedContactPersonId]);

  const handleSupplierChange = useCallback(
    (value: string) => {
      form.setFieldsValue({ supplierId: value, contactPersonId: undefined, languageId: undefined });
      setSelectedSupplierId(value);
    },
    [form]
  );

  const handleContactPersonChange = useCallback(
    (value: string) => {
      const selectedPerson = contactPersons.find((person: any) => person.value === value);
      form.setFieldsValue({ contactPersonId: value });
      setSelectedContactPersonId(value);

      // If contact person has no email, show warning
      if (selectedPerson && !selectedPerson.contacts?.EMAIL?.length) {
        message.warning('Selected contact person has no email address');
      }

      if (selectedPerson?.language) {
        form.setFieldValue('languageId', selectedPerson?.language?.id);
        setSelectedLanguageId(selectedPerson?.language?.id);
      }
    },
    [contactPersons, form]
  );

  const handleAddSupplier = useCallback(async () => {
    const supplierId = form.getFieldValue('supplierId');
    const contactPersonId = form.getFieldValue('contactPersonId');
    const languageId = form.getFieldValue('languageId');

    const alreadyExists = suppliers.find((i) => i.supplierId === supplierId);
    if (alreadyExists) {
      notificationApi({ type: 'error', message: 'This Supplier Already Selected' });
      return;
    }

    const contactPersonName = contactPersons.find((opt: any) => opt.value === contactPersonId)?.label;
    const supplierName = allSupplierOptions.find((opt: any) => opt.value === supplierId)?.label;
    const { label: languageName, flagLocation } = languagesList.find((opt: any) => opt.value === languageId);

    if (!supplierId || !contactPersonId || !languageId) {
      message.warning('Please select supplier, contact person and language');
      return;
    }

    const newSupplier = {
      supplierId,
      contactPersonId,
      languageId,
      supplierName,
      contactPersonName,
      languageName,
      flagLocation,
    };

    setSuppliers([...suppliers, newSupplier]);

    await onSupplierAdded(supplierId, contactPersonId, languageId);

    form.resetFields(['supplierId', 'contactPersonId', 'languageId']);
    setSelectedSupplierId('');
    setSelectedContactPersonId('');
    setSelectedLanguageId('');
    setDisabledSuppActions(true);
  }, [suppliers, contactPersons, allSupplierOptions, languagesList, form, onSupplierAdded, notificationApi]);

  const handleRemoveSupplier = useCallback(
    (index: number) => {
      const updatedSuppliers = [...suppliers];
      updatedSuppliers.splice(index, 1);
      setSuppliers(updatedSuppliers);
      return updatedSuppliers;
    },
    [suppliers]
  );

  const resetForm = useCallback(() => {
    form.resetFields();
    handleSupplierChange('');
    setDisabledSuppActions(true);
  }, [form, handleSupplierChange]);

  return {
    suppliers,
    supplierOptions,
    filteredSuppliers,
    contactPersons,
    loadingContactPersons,
    selectedSupplierId,
    disabledSuppActions,
    openSupplierModal,
    setOpenSupplierModal,
    openContactPersonModal,
    setOpenContactPersonModal,
    handleSupplierChange,
    handleContactPersonChange,
    handleAddSupplier,
    handleRemoveSupplier,
    resetForm,
  };
};
